"use client";

import { Check, ChevronRight, Shield, Sparkles } from "lucide-react";
import { PublicLink as Link } from "@/components/public/PublicLink";
import { Button } from "@/components/ui/button";
import { PlatformLogo } from "@/components/ui/platform-logo";
import { useJsonTranslation } from "@/hooks/useJsonTranslation";
import type { PlatformId } from "@/types/platforms";

const CTA_PLATFORMS: PlatformId[] = [
  "youtube",
  "tiktok",
  "instagram",
  "facebook",
  "linkedin",
  "x",
];

export function FinalCTA() {
  const { t } = useJsonTranslation("HomeFinalCta");

  return (
    <div
      data-home-block="final-cta-panel"
      className="home-showcase-panel px-5 py-10 sm:px-8 sm:py-14"
    >
      {/* Layered luxury background: branded field + soft grid */}
      <div className="pointer-events-none absolute inset-0">
        <div
          className="hero-grid absolute inset-0 opacity-40"
          aria-hidden="true"
        />
      </div>

      <div className="relative z-10 mx-auto max-w-3xl text-center">
        {/* Badge */}
        <div className="home-badge text-leaf-600 dark:text-leaf-400 mb-8">
          <Sparkles className="h-4 w-4" aria-hidden="true" />
          {t("badge")}
        </div>

        {/* Headline */}
        <h2 className="home-section-title mb-6 text-4xl text-balance md:text-5xl lg:text-6xl">
          <span className="block">{t("titleLine1")}</span>
          <span className="text-gradient-primary mt-1 block">
            {t("titleLine2")}
          </span>
        </h2>

        {/* Subheadline */}
        <p className="home-section-copy mx-auto mb-8 max-w-xl text-lg text-pretty sm:text-xl">
          {t("subtitle")}
        </p>

        {/* 6-platform icon row — reinforces scope */}
        <div className="mb-10 flex flex-wrap items-center justify-center gap-2.5">
          {CTA_PLATFORMS.map((p) => (
            <div
              key={p}
              className="home-chip flex h-10 w-10 items-center justify-center p-0 transition-transform duration-300 hover:-translate-y-0.5"
              aria-hidden="true"
            >
              <PlatformLogo platform={p} size={20} className="opacity-90" />
            </div>
          ))}
        </div>

        {/* Primary CTA + demoted secondary link */}
        <div className="flex flex-col items-center justify-center gap-4 sm:flex-row sm:gap-6">
          <Button
            asChild
            variant="outline"
            size="xl"
            className="home-outline-action group"
          >
            <Link href="/signup">
              {t("ctaGetStarted")}
              <ChevronRight
                className="ml-1 h-5 w-5 transition-transform group-hover:translate-x-1"
                aria-hidden="true"
              />
            </Link>
          </Button>
          <Link
            href="/seeds"
            className="home-ghost-link inline-flex min-h-[44px] items-center gap-1.5 underline-offset-4 hover:underline"
          >
            {t("ctaViewPricing")}
          </Link>
        </div>

        {/* Risk-reversal trio — replaces the old single disclaimer line */}
        <div className="mx-auto mt-10 grid max-w-2xl gap-3 sm:grid-cols-3">
          <div className="glass flex items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm">
            <Check
              className="text-leaf-500 h-4 w-4 shrink-0"
              aria-hidden="true"
            />
            <span className="text-foreground/80 font-medium">
              {t("risk.noCreditCard")}
            </span>
          </div>
          <div className="glass flex items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm">
            <Shield
              className="text-leaf-500 h-4 w-4 shrink-0"
              aria-hidden="true"
            />
            <span className="text-foreground/80 font-medium">
              {t("risk.moneyBack")}
            </span>
          </div>
          <div className="glass flex items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm">
            <Sparkles
              className="text-leaf-500 h-4 w-4 shrink-0"
              aria-hidden="true"
            />
            <span className="text-foreground/80 font-medium">
              {t("risk.cancelAnytime")}
            </span>
          </div>
        </div>

        <a
          href="https://x.com/VidSeeds"
          target="_blank"
          rel="noopener noreferrer"
          className="home-chip mt-8 inline-flex items-center gap-2 transition-all duration-200"
        >
          <PlatformLogo platform="x" size={16} />
          {t("followOnX")}
        </a>
      </div>
    </div>
  );
}
